/**
 * Modèle de présentation « type Google Slides ».
 * Toutes les positions/tailles sont exprimées en POUR CENTAGE du canvas
 * (0,0 = coin supérieur gauche), ce qui rend le rendu indépendant de la taille
 * d'affichage. L'empilement (axe Z) suit l'ordre du tableau `elements`
 * (le dernier élément est au premier plan).
 */

export type DeckElementType = 'text' | 'image' | 'shape' | 'line' | 'table' | 'chart' | 'video' | 'wordart';

export type DeckShape = 'rect' | 'roundRect' | 'ellipse' | 'triangle' | 'diamond' | 'arrow' | 'star';

export interface DeckChart {
  kind: 'bar' | 'pie' | 'line';
  title: string;
  categories: string[];
  values: number[];
}

export interface DeckTable {
  headers: string[];
  rows: string[][];
}

export interface DeckElement {
  id: string;
  type: DeckElementType;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
  opacity?: number;
  /** Texte riche (HTML simple) pour les zones de texte / wordart. */
  html?: string;
  src?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  shape?: DeckShape;
  radius?: number;
  color?: string;
  /** Taille de police en % de la largeur du canvas (ex. 4 = 4 %). */
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  align?: 'left' | 'center' | 'right';
  valign?: 'top' | 'middle' | 'bottom';
  link?: string;
  table?: DeckTable;
  chart?: DeckChart;
  videoUrl?: string;
  /** Ombre portée douce. */
  shadow?: boolean;
  /** Animation d'apparition. */
  anim?: 'none' | 'fade' | 'appear' | 'zoom' | 'rotate';
}

export type DeckTransition = 'none' | 'fade' | 'slide' | 'zoom' | 'dissolve';

export interface DeckSlide {
  id: string;
  title: string;
  bg: string;
  bgImage?: string;
  transition?: DeckTransition;
  notes?: string;
  elements: DeckElement[];
}

export interface DeckTheme {
  primary: string;
  accent: string;
  background: string;
  text: string;
  headingFont: string;
  bodyFont: string;
}

export interface SlideDeck {
  version: 2;
  w: number;
  h: number;
  theme: DeckTheme;
  slides: DeckSlide[];
}

export const DEFAULT_THEME: DeckTheme = {
  primary: '#0f766e',
  accent: '#c2410c',
  background: '#ffffff',
  text: '#1f2937',
  headingFont: "'Fraunces', Georgia, serif",
  bodyFont: "'Inter', system-ui, sans-serif",
};

export const THEME_PRESETS: { id: string; label: string; theme: DeckTheme }[] = [
  {
    id: 'emeraude',
    label: 'Émeraude',
    theme: { ...DEFAULT_THEME },
  },
  {
    id: 'sable',
    label: 'Sable',
    theme: {
      primary: '#92400e',
      accent: '#0f766e',
      background: '#fbf7f0',
      text: '#292524',
      headingFont: "'Fraunces', Georgia, serif",
      bodyFont: "'Inter', system-ui, sans-serif",
    },
  },
  {
    id: 'ardoise',
    label: 'Ardoise',
    theme: {
      primary: '#334155',
      accent: '#9a3412',
      background: '#f1f5f9',
      text: '#0f172a',
      headingFont: "'Space Grotesk', system-ui, sans-serif",
      bodyFont: "'Inter', system-ui, sans-serif",
    },
  },
  {
    id: 'nuit',
    label: 'Nuit',
    theme: {
      primary: '#fbbf24',
      accent: '#22d3ee',
      background: '#111827',
      text: '#f8fafc',
      headingFont: "'Space Grotesk', system-ui, sans-serif",
      bodyFont: "'Inter', system-ui, sans-serif",
    },
  },
];

export const BG_PRESETS = ['#ffffff', '#fbf7f0', '#f1f5f9', '#ecfdf5', '#fef3c7', '#111827', '#1f2937', '#0f172a'];

export function uid(prefix = 'el'): string {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  return `${prefix}-${rand}`;
}

export function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

export function isDarkSlide(slide: DeckSlide): boolean {
  const bg = (slide.bg || '#ffffff').replace('#', '');
  if (bg.length !== 6) return false;
  const r = parseInt(bg.slice(0, 2), 16);
  const g = parseInt(bg.slice(2, 4), 16);
  const b = parseInt(bg.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

export function textElement(
  html: string,
  patch: Partial<DeckElement> = {},
  theme: DeckTheme = DEFAULT_THEME,
): DeckElement {
  return {
    id: uid('text'),
    type: 'text',
    x: 8,
    y: 10,
    w: 84,
    h: 22,
    html,
    fontSize: 5,
    color: theme.text,
    align: 'left',
    valign: 'top',
    ...patch,
  };
}

export function emptySlide(theme: DeckTheme = DEFAULT_THEME, bg?: string): DeckSlide {
  return {
    id: uid('slide'),
    title: 'Nouvelle diapositive',
    bg: bg || theme.background,
    transition: 'fade',
    notes: '',
    elements: [
      textElement('<b>Titre de la diapositive</b>', { y: 32, h: 18, fontSize: 6.4, align: 'center' }, theme),
      textElement('Sous-titre ou idée clé', { y: 52, h: 12, fontSize: 3, align: 'center', color: theme.primary }, theme),
    ],
  };
}

export const LAYOUTS: { id: string; label: string; icon: string }[] = [
  { id: 'title', label: 'Titre', icon: 'ri-h-1' },
  { id: 'titleBody', label: 'Titre + contenu', icon: 'ri-layout-top-2-line' },
  { id: 'twoCol', label: 'Deux colonnes', icon: 'ri-layout-column-line' },
  { id: 'imageRight', label: 'Image à droite', icon: 'ri-layout-right-line' },
  { id: 'section', label: 'Section', icon: 'ri-separator' },
  { id: 'blank', label: 'Vierge', icon: 'ri-square-line' },
];

export function buildLayout(id: string, theme: DeckTheme, bg: string): DeckSlide {
  const slide = emptySlide(theme, bg);
  switch (id) {
    case 'title':
      slide.elements = [
        textElement('<b>Présentation</b>', { x: 10, y: 34, w: 80, h: 20, fontSize: 8, align: 'center' }, theme),
        textElement('Votre nom · date', { x: 10, y: 56, w: 80, h: 8, fontSize: 2.6, align: 'center', color: theme.primary }, theme),
      ];
      break;
    case 'titleBody':
      slide.elements = [
        textElement('<b>Titre</b>', { x: 7, y: 8, w: 86, h: 14, fontSize: 5, color: theme.primary }, theme),
        textElement('Votre contenu ici…', { x: 7, y: 26, w: 86, h: 60, fontSize: 3 }, theme),
      ];
      break;
    case 'twoCol':
      slide.elements = [
        textElement('<b>Titre</b>', { x: 7, y: 8, w: 86, h: 12, fontSize: 4.6, color: theme.primary }, theme),
        textElement('Colonne 1', { x: 7, y: 24, w: 41, h: 62, fontSize: 2.8 }, theme),
        textElement('Colonne 2', { x: 52, y: 24, w: 41, h: 62, fontSize: 2.8 }, theme),
      ];
      break;
    case 'imageRight':
      slide.elements = [
        textElement('<b>Titre</b>', { x: 7, y: 8, w: 45, h: 14, fontSize: 4.6, color: theme.primary }, theme),
        textElement('Votre texte…', { x: 7, y: 26, w: 45, h: 58, fontSize: 2.8 }, theme),
        {
          id: uid('shape'),
          type: 'shape',
          x: 56,
          y: 12,
          w: 38,
          h: 72,
          fill: theme.accent,
          opacity: 0.14,
          shape: 'roundRect',
          radius: 14,
        },
      ];
      break;
    case 'section':
      slide.elements = [
        textElement('<b>Section</b>', { x: 10, y: 40, w: 80, h: 18, fontSize: 7, align: 'center', color: theme.primary }, theme),
      ];
      break;
    default:
      slide.elements = [];
      break;
  }
  return slide;
}

/** Convertit un ancien contenu { slides: [{title, body}] } en deck. */
export function deckFromContent(content: Record<string, unknown> | null | undefined): SlideDeck {
  if (content && typeof content.deck === 'object' && content.deck) {
    return normalizeDeck(content.deck as Partial<SlideDeck>);
  }
  const legacy = Array.isArray(content?.slides) ? (content?.slides as Record<string, unknown>[]) : [];
  const theme = DEFAULT_THEME;
  const slides: DeckSlide[] = legacy.map((s, index) => {
    const elements: DeckElement[] = [];
    if (s.title) {
      elements.push(textElement(`<b>${escapeHtml(String(s.title))}</b>`, { x: 8, y: 10, w: 84, h: 16, fontSize: 5.2, color: theme.primary }, theme));
    }
    if (s.body) {
      elements.push(
        textElement(escapeHtml(String(s.body)).replace(/\n/g, '<br>'), { x: 8, y: 30, w: 84, h: 58, fontSize: 3 }, theme),
      );
    }
    if (Array.isArray(s.elements) && s.elements.length) {
      (s.elements as DeckElement[]).forEach((el) => elements.push({ ...el, id: el.id || uid() }));
    }
    return {
      id: String(s.id || uid('slide')) || uid('slide'),
      title: String(s.title || `Diapositive ${index + 1}`),
      bg: String(s.bg || theme.background),
      bgImage: typeof s.bgImage === 'string' ? s.bgImage : undefined,
      transition: 'fade',
      notes: '',
      elements,
    };
  });
  return {
    version: 2,
    w: 960,
    h: 540,
    theme,
    slides: slides.length ? slides : [buildLayout('title', theme, theme.background)],
  };
}

function normalizeDeck(raw: Partial<SlideDeck>): SlideDeck {
  const theme = { ...DEFAULT_THEME, ...(raw.theme || {}) };
  const slides = Array.isArray(raw.slides) && raw.slides.length ? raw.slides : [buildLayout('title', theme, theme.background)];
  return {
    version: 2,
    w: Number(raw.w) || 960,
    h: Number(raw.h) || 540,
    theme,
    slides: slides.map((s, i) => ({
      id: s.id || uid('slide'),
      title: s.title || `Diapositive ${i + 1}`,
      bg: s.bg || theme.background,
      bgImage: s.bgImage,
      transition: s.transition || 'fade',
      notes: s.notes || '',
      elements: (s.elements || []).map((el) => normalizeElement(el, theme)),
    })),
  };
}

export function normalizeElement(el: DeckElement, theme: DeckTheme): DeckElement {
  return {
    id: el.id || uid(),
    type: el.type || 'text',
    x: Number.isFinite(el.x) ? el.x : 8,
    y: Number.isFinite(el.y) ? el.y : 8,
    w: Number.isFinite(el.w) ? el.w : 40,
    h: Number.isFinite(el.h) ? el.h : 20,
    rotation: Number(el.rotation) || 0,
    opacity: el.opacity === undefined ? 1 : el.opacity,
    html: el.html || '',
    src: el.src,
    fill: el.fill,
    stroke: el.stroke,
    strokeWidth: el.strokeWidth,
    shape: el.shape || 'rect',
    radius: el.radius,
    color: el.color || theme.text,
    fontSize: el.fontSize || 3,
    bold: el.bold,
    italic: el.italic,
    underline: el.underline,
    align: el.align || 'left',
    valign: el.valign || 'top',
    link: el.link,
    table: el.table,
    chart: el.chart,
    videoUrl: el.videoUrl,
    shadow: el.shadow,
    anim: el.anim || 'none',
  };
}

/** Conserve la compatibilité des exports (PPTX/PDF) en dérivant titre + corps. */
export function deckToLegacySlides(deck: SlideDeck): { id: string; title: string; body: string; bg?: string }[] {
  return deck.slides.map((s) => {
    const texts = s.elements.filter((e) => e.type === 'text' || e.type === 'wordart');
    const strip = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    const title = strip(texts[0]?.html || '') || s.title;
    const body = texts
      .slice(1)
      .map((e) => strip(e.html || ''))
      .filter(Boolean)
      .join('\n');
    return { id: s.id, title, body, bg: s.bg };
  });
}

export function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Extrait un deck depuis une réponse IA JSON (structure simplifiée acceptée). */
export function parseDeckFromAi(raw: unknown): DeckSlide[] {
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;
  const slidesRaw = Array.isArray(obj.slides) ? obj.slides : Array.isArray(obj.diapositives) ? obj.diapositives : [];
  const theme = DEFAULT_THEME;
  const slides: DeckSlide[] = [];
  slidesRaw.forEach((s, index) => {
    if (!s || typeof s !== 'object') return;
    const slide = s as Record<string, unknown>;
    const elementsRaw = Array.isArray(slide.elements) ? slide.elements : [];
    const elements: DeckElement[] = [];
    elementsRaw.forEach((e) => {
      if (!e || typeof e !== 'object') return;
      const el = e as Record<string, unknown>;
      const type = String(el.type || 'text') as DeckElementType;
      const base: DeckElement = {
        id: uid(type),
        type: ['text', 'image', 'shape', 'line', 'table', 'chart', 'video', 'wordart'].includes(type) ? type : 'text',
        x: Number(el.x) || 8,
        y: Number(el.y) || 8,
        w: Number(el.w) || 40,
        h: Number(el.h) || 20,
        rotation: Number(el.rotation) || 0,
        html: typeof el.html === 'string' ? el.html : typeof el.text === 'string' ? escapeHtml(el.text) : '',
        src: typeof el.src === 'string' ? el.src : undefined,
        fill: typeof el.fill === 'string' ? el.fill : undefined,
        color: typeof el.color === 'string' ? el.color : theme.text,
        fontSize: Number(el.fontSize) || 3,
        align: (el.align as DeckElement['align']) || 'left',
        valign: (el.valign as DeckElement['valign']) || 'top',
        shape: (el.shape as DeckShape) || 'rect',
        radius: Number(el.radius) || 0,
        link: typeof el.link === 'string' ? el.link : undefined,
        videoUrl: typeof el.videoUrl === 'string' ? el.videoUrl : undefined,
        opacity: el.opacity === undefined ? 1 : Number(el.opacity),
      };
      if (el.chart && typeof el.chart === 'object') {
        const c = el.chart as Record<string, unknown>;
        base.type = 'chart';
        base.chart = {
          kind: c.kind === 'pie' || c.kind === 'line' ? (c.kind as 'pie' | 'line') : 'bar',
          title: String(c.title || 'Graphique'),
          categories: Array.isArray(c.categories) ? c.categories.map(String) : [],
          values: Array.isArray(c.values) ? c.values.map(Number).filter((v) => Number.isFinite(v)) : [],
        };
      }
      if (el.table && typeof el.table === 'object') {
        const t = el.table as Record<string, unknown>;
        base.type = 'table';
        base.table = {
          headers: Array.isArray(t.headers) ? t.headers.map(String) : [],
          rows: Array.isArray(t.rows) ? (t.rows as unknown[]).map((r) => (Array.isArray(r) ? r.map(String) : [String(r)])) : [],
        };
      }
      elements.push(base);
    });
    slides.push({
      id: uid('slide'),
      title: String(slide.title || `Diapositive ${index + 1}`),
      bg: String(slide.bg || theme.background),
      transition: 'fade',
      notes: String(slide.notes || ''),
      elements: elements.length ? elements : buildLayout('titleBody', theme, theme.background).elements,
    });
  });
  return slides;
}

/** Découpe un texte hiérarchisé en diapositives simples (repli sans JSON). */
export function slidesFromPlainText(text: string, theme: DeckTheme): DeckSlide[] {
  const blocks = text
    .split(/\n{2,}|\n(?=#)/)
    .map((b) => b.trim())
    .filter(Boolean)
    .slice(0, 12);
  if (!blocks.length) return [buildLayout('title', theme, theme.background)];
  return blocks.map((block, index) => {
    const lines = block.split('\n');
    const isHeading = /^#/.test(lines[0]) || lines[0].length < 60;
    const title = lines[0].replace(/^#+\s*/, '');
    const body = lines.slice(1).join('\n');
    if (index === 0 && !body) {
      return {
        id: uid('slide'),
        title,
        bg: theme.background,
        transition: 'fade',
        elements: [textElement(`<b>${escapeHtml(title)}</b>`, { x: 10, y: 36, w: 80, h: 20, fontSize: 8, align: 'center' }, theme)],
      };
    }
    return {
      id: uid('slide'),
      title,
      bg: theme.background,
      transition: 'fade',
      elements: [
        textElement(`<b>${escapeHtml(title)}</b>`, { x: 7, y: 8, w: 86, h: 14, fontSize: isHeading ? 4.8 : 4, color: theme.primary }, theme),
        textElement(escapeHtml(body || '').replace(/\n/g, '<br>'), { x: 7, y: 26, w: 86, h: 60, fontSize: 2.9 }, theme),
      ],
    };
  });
}